// UploadQueue.js

import React, { useState,useContext } from 'react';
import { Paper, Typography, Box, IconButton, Collapse } from '@mui/material';
import { styled } from '@mui/material/styles';
import LinearProgress, { linearProgressClasses } from '@mui/material/LinearProgress';
import { Close, ExpandLess, ExpandMore, CheckCircle, Error as ErrorIcon } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { v4 as uuidv4 } from 'uuid';
import axios from 'axios';
import { AuthContext } from "../context/AuthContext";
import { CloudContext } from "../context/CloudContext";
import { uploadS3 } from "../utils/interfaceS3";
import { uploadToCesiumS3,uploadPhotosToCesiumS3 } from "../utils/cesiumManagement";
import { newActivity } from "../utils/activityManagement";
import { createProcessingRequestIds } from "../utils/requestManagement";
import { useTranslation } from "react-i18next";
import config from '../config.js';

const BorderLinearProgress = styled(LinearProgress)(({ theme }) => ({
    height: 10,
    borderRadius: 8,
    [`&.${linearProgressClasses.colorPrimary}`]: {
      backgroundColor: theme.palette.grey[theme.palette.mode === 'light' ? 200 : 800],
    },
    [`& .${linearProgressClasses.bar}`]: {
      borderRadius: 8,
      backgroundColor: theme.palette.mode === 'light' ? '#0174BE' : '#308fe8',
    },
  }));

function UploadQueue(props) {
  const {
    user,
    idToken,
    jwt,
    region,
    idpool,
    cloudinary_cloud,
    cloudinary_preset,
    projectSelected,
    reloadProjects,
    userId,
  } = props;

  const {  t } = useTranslation();
  const {service} = useContext(AuthContext);
  const {cesium_token} = useContext(CloudContext);
  

  const { enqueueSnackbar } = useSnackbar();
  const [uploadQueue, setUploadQueue] = useState([]);
  const [isVisible, setIsVisible] = useState(true);
  const [isUploadQueueOpen, setIsUploadQueueOpen] = useState(true);
  const hasOngoingUploads = uploadQueue.some(item => item.status === 'uploading');

  const computeAggregateProgress = (item) => {
    const segments = [];
    if (item.hasPointCloud) {
      segments.push(item.pointCloudProgress ?? 0);
    }
    if (item.hasPhotos) {
      segments.push(item.photosProgress ?? 0);
    }
    if (segments.length === 0) {
      return item.progress ?? 0;
    }
    const aggregate = segments.reduce((sum, value) => sum + value, 0) / segments.length;
    return Math.round(aggregate);
  };

  const updateAggregatedProgress = (id, updates = {}) => {
    setUploadQueue((prevQueue) =>
      prevQueue.map((item) => {
        if (item.id !== id) {
          return item;
        }
        const nextItem = { ...item, ...updates };
        nextItem.progress = computeAggregateProgress(nextItem);
        return nextItem;
      })
    );
  };

  const addToUploadQueue = (uploadItems) => {
    // Separate archive items from regular items
    const archiveUploads = uploadItems.filter(item => item.type === 'archive');
    const regularUploads = uploadItems.filter(item => item.type !== 'archive');

    // Process archive uploads
    const newArchiveUploads = archiveUploads
      .filter(item => item.file)
      .map((item) => ({
        id: uuidv4(),
        type: 'archive',
        fileName: item.file.name,
        displayName: `${item.file.name}`,
        progress: 0,
        status: 'uploading',
        file: item.file,
        description: item.description,
      }));

    // Process regular uploads (existing logic)
    const validRegularUploads = regularUploads.filter(
      (item) => item.file || (Array.isArray(item.photos) && item.photos.length > 0)
    );

    const newRegularUploads = validRegularUploads.map((item) => {
      const photos = Array.isArray(item.photos) ? item.photos : [];
      const hasPointCloud = Boolean(item.file);
      const epsgData = item.epsgData;
      const hasPhotos = photos.length > 0;
      const totalUnits = (hasPointCloud ? 1 : 0) + (hasPhotos ? 1 : 0) || 1;
      const displayName = hasPointCloud
        ? hasPhotos
          ? `${item.file.name} + ${photos.length} ${t("Photos")}`
          : item.file.name
        : `${t("Photos")} (${photos.length})`;

      return {
        id: uuidv4(),
        fileName: item.file ? item.file.name : null,
        displayName,
        progress: 0,
        status: 'uploading',
        file: item.file || null,
        epsgData,
        photos,
        imageFile: item.imageFile || null,
        description: item.description,
        processings: item.processings,
        subprocessings: item.subprocessings,
        hasPointCloud,
        hasPhotos,
        totalUnits,
        pointCloudProgress: hasPointCloud ? 0 : null,
        photosProgress: hasPhotos ? 0 : null,
      };
    });

    const allNewUploads = [...newArchiveUploads, ...newRegularUploads];

    if (allNewUploads.length === 0) {
      return;
    }

    setUploadQueue((prevQueue) => [...prevQueue, ...allNewUploads]);

    allNewUploads.forEach((uploadItem) => uploadFile(uploadItem));
  };

  const uploadFile = async (uploadItem) => {
    setIsVisible(true);

    const { id, type, hasPointCloud, hasPhotos } = uploadItem;

    try {
      if (type === 'archive') {
        // Archive-specific upload flow
        await uploadArchive(uploadItem, (progress) =>
          setUploadQueue((prev) =>
            prev.map((item) =>
              item.id === id ? { ...item, progress } : item
            )
          )
        );
        await finalizeArchiveRecord(uploadItem);
      } else {
        // Existing point cloud/photos flow
        const [pointCloudResult, photosResult] = await Promise.all([
          hasPointCloud
            ? uploadPointCloud(uploadItem, (progress) =>
                updateAggregatedProgress(id, { pointCloudProgress: progress })
              )
            : Promise.resolve(null),
          hasPhotos
            ? uploadPhotos(uploadItem, (progress) =>
                updateAggregatedProgress(id, { photosProgress: progress })
              )
            : Promise.resolve(null),
        ]);

        await finalizeDataRecord(uploadItem, {
          idCesium: pointCloudResult?.idCesium ?? null,
          optimizeImage: pointCloudResult?.optimizeImage ?? null,
          idCesiumGs: photosResult?.idCesiumGs ?? null,
        });
      }

      // Mark completed
      setUploadQueue((prevQueue) =>
        prevQueue.map((item) =>
          item.id === id
            ? {
                ...item,
                status: 'completed',
                progress: 100,
                pointCloudProgress: item.hasPointCloud ? 100 : item.pointCloudProgress,
                photosProgress: item.hasPhotos ? 100 : item.photosProgress,
              }
            : item
        )
      );
    } catch (error) {
      console.error(error);
      enqueueSnackbar(`${t("ErrorUpload")}`, { variant: 'error' });
      setUploadQueue((prevQueue) =>
        prevQueue.map((item) =>
          item.id === id
            ? {
                ...item,
                status: 'error',
                progress: 0,
                pointCloudProgress: item.hasPointCloud ? 0 : item.pointCloudProgress,
                photosProgress: item.hasPhotos ? 0 : item.photosProgress,
              }
            : item
        )
      );
    }
  };

  const uploadPointCloud = async (uploadItem, onProgress) => {
    const { file, imageFile, description } = uploadItem;

    if (!file) {
      return null;
    }

    let optimizeImage = null;
    if (imageFile) {
      optimizeImage = await uploadImage(imageFile);
    }

    const progressHandler = (progressValue) => {
      if (typeof progressValue === 'number') {
        onProgress?.(progressValue);
      }
    };

    let id_cesium = null;
    if (service === "classic") {
      await uploadS3(user.sub, idToken, region, idpool, file.name, file, progressHandler);
    } else if (service === "mapping") {
      id_cesium = await uploadToCesiumS3(file.name, description, file, cesium_token, progressHandler);
    }

    onProgress?.(100);
    return { idCesium: id_cesium, optimizeImage };
  };

  const uploadPhotos = async (uploadItem, onProgress) => {
    const photos = uploadItem.photos || [];
    if (!photos.length) {
      return null;
    }

    const progressHandler = (progressValue) => {
      if (typeof progressValue === 'number') {
        onProgress?.(progressValue);
      }
    };

    onProgress?.(0);
    const id_cesium_gs = await uploadPhotosToCesiumS3(
      photos[0]?.name || `photos-${Date.now()}`,
      photos,
      cesium_token,
      progressHandler
    );
    onProgress?.(100);
    console.log(id_cesium_gs)
    return { idCesiumGs: id_cesium_gs };
  };

  const uploadArchive = async (uploadItem, onProgress) => {
    const { file } = uploadItem;
    if (!file) {
      return null;
    }

    const progressHandler = (progressValue) => {
      if (typeof progressValue === 'number') {
        onProgress?.(progressValue);
      }
    };

    // Upload to S3 only (never to Cesium)
    await uploadS3(
      user.sub,
      idToken,
      region,
      idpool,
      file.name,
      file,
      progressHandler
    );

    onProgress?.(100);
    return { filename: file.name };
  };

  const finalizeArchiveRecord = async (uploadItem) => {
    const { file, description } = uploadItem;

    const dataForm = new FormData();
    const data = {
      filename: file.name,
      size: file.size || 0,
      description: description || '',
      viewable: false,
      data_type: 'archive',
    };
    dataForm.append('data', JSON.stringify(data));

    const response = await saveData(dataForm);
    await updateProjectWithNewData(projectSelected.id, response.data.data.id);
    newActivity(userId, jwt, file.name, file.size || 0, "upload", service);
    reloadProjects();
  };

  const createTransformation = async ({ tx = 0, ty = 0, tz = 0, rx = 0, ry = 0, rz = 0, sx = 1, sy = 1, sz = 1 } = {}) => {
    const form = new FormData();
    form.append('data', JSON.stringify({ tx, ty, tz, rx, ry, rz, sx, sy, sz }));
    const response = await axios.post(`${config.apiUrl}/api/transformations`, form, {
      headers: { Authorization: `Bearer ${jwt}` },
    });
    return response.data.data.id;
  };

  const finalizeDataRecord = async (
    uploadItem,
    { idCesium = null, idCesiumGs = null, optimizeImage = null }
  ) => {
    const { file, description, processings = [], photos = [], epsgData, hasPointCloud, hasPhotos } = uploadItem;

    const filename = file?.name || photos[0]?.name || `photos-${Date.now()}`;
    const fileSize = file?.size || 0;
    const photosSize = photos.reduce((total, photo) => total + (photo.size || 0), 0);
    const size = +fileSize + +photosSize;

    const processingRequestIds = await createProcessingRequestIds(jwt, processings);

    let transformationPointsId = null;
    let transformationGsId = null;

    if (hasPointCloud) {
      transformationPointsId = await createTransformation({ tz: epsgData?.zTranslation ?? 0 });
    }

    if (hasPhotos) {
      transformationGsId = await createTransformation();
    }

    const dataForm = createDataForm(
      filename,
      size || 0,
      description,
      optimizeImage,
      processingRequestIds,
      idCesium,
      idCesiumGs,
      transformationPointsId,
      transformationGsId
    );

    console.log(dataForm)

    const response = await saveData(dataForm);
    await updateProjectWithNewData(projectSelected.id, response.data.data.id);
    newActivity(userId, jwt, filename, size || 0, "upload", service);
    reloadProjects();
  };

  const uploadImage = async (imageFile) => {
    const formData = new FormData();
    formData.append("file", imageFile);
    formData.append("upload_preset", cloudinary_preset);

    const response = await axios.post(`https://api.cloudinary.com/v1_1/${cloudinary_cloud}/image/upload`, formData);
    return response.data.secure_url;
  };

  const createDataForm = (filename, size, description, optimizeImage = null, processingIds, id = null, id_gs = null, transformationPointsId = null, transformationGsId = null) => {
    const dataForm = new FormData();
    const data = {
      filename: filename,
      size: size,
      description: description,
      viewable: false,
      processing_requests: processingIds,
    };
    if (optimizeImage) {
      data.imageUrl = optimizeImage;
    }
    if (id) {
      data.id_cesium = id;
    }
    if (id_gs) {
      data.id_cesium_gs = id_gs;
    }
    if (transformationPointsId != null) {
      data.transformation_cesium_points = transformationPointsId;
    }
    if (transformationGsId != null) {
      data.transformation_cesium_gs = transformationGsId;
    }
    dataForm.append('data', JSON.stringify(data));
    return dataForm;
  };

  const saveData = async (dataForm) => {
    let response = await axios.post(`${config.apiUrl}/api/datas`, dataForm, {
      headers: {
        Authorization: `Bearer ${jwt}`,
      }
    });
    return response;
  };

  const updateProjectWithNewData = async (idProject, idData) => {
    let project = await axios.get(`${config.apiUrl}/api/projects/${idProject}?populate=*`, {
      headers: {
        Authorization: `Bearer ${jwt}`,
      },
    });

    // Add the new data ID to the project's data list
    let newDataIds = project.data.data.attributes.data.data.map((item) => item.id);
    newDataIds.push(idData);

    const dataForm = new FormData();
    dataForm.append('data', JSON.stringify({
      data: newDataIds,
    }));
    await axios.put(`${config.apiUrl}/api/projects/${idProject}`, dataForm, {
      headers: {
        'Authorization': `Bearer ${jwt}`,
        'Content-Type': 'multipart/form-data'
      }
    });
  };

  return (
    <>
      {isVisible && uploadQueue.length > 0 && (
        <Paper
        sx={{
            position: 'fixed',
            borderRadius: 3,
            bottom: '15px',
            right: '15px',
            width: {
              xs: '90%', // 90% width on extra-small screens
              sm: '70%', // 70% width on small screens
              md: '50%', // 50% width on medium screens
              lg: '40%', // 40% width on large screens
              xl: '30%', // 30% width on extra-large screens
            },
            maxHeight: '40vh',
            overflowY: 'auto',
            padding: 2,
            backgroundColor: 'background.paper',
            zIndex: 1300,
          }}
          elevation={24}
        >
        <Box display="flex" justifyContent="space-between" alignItems="center" sx={{p:1}}>
            <Typography variant="h6">{t("UploadQueue")}</Typography>
            <Box>
            <IconButton size="small" onClick={() => setIsUploadQueueOpen(!isUploadQueueOpen)}>
                {isUploadQueueOpen ? <ExpandMore /> : <ExpandLess />}
            </IconButton>
            {!hasOngoingUploads && (
            <IconButton size="small" onClick={() => {setUploadQueue([]);setIsVisible(false);}}>
                <Close />
            </IconButton>)}
            </Box>
        </Box>
          <Collapse in={isUploadQueueOpen}>
          {uploadQueue.map((item) => (
            <Box key={item.id} mb={1} display="flex" alignItems="center">
                {/* Icon based on status */}
                {item.status === 'completed' && (
                <CheckCircle fontSize="small" style={{ color: 'green', marginRight: '8px' }} />
                )}
                {item.status === 'error' && (
                <ErrorIcon fontSize="small" style={{ color: 'red', marginRight: '8px' }} />
                )}
                {/* Filename and progress */}
                <Box flexGrow={1}>
                <Typography variant="body2">{item.displayName || item.fileName}</Typography>
                <BorderLinearProgress variant="determinate" value={item.progress} />
                <Typography variant="caption" color="textSecondary">
                    {item.status === 'completed'
                    ? t("UploadCompleted")
                    : item.status === 'error'
                    ? t("UploadFailed")
                    : `${t("Uploading")}... ${item.progress}%`}
                </Typography>
                </Box>
            </Box>
            ))}
          </Collapse>
        </Paper>
      )}

      {/* This is how we'll expose the addToUploadQueue function */}
      {props.children({ addToUploadQueue })}
    </>
  );
}

export default UploadQueue;
